/**
 * Renders a markdown + LaTeX + code string. Two variants:
 *   <QuestionBody>  — question stems and solutions, block-level
 *   <MarkdownInline> — option labels, sits inline next to a radio/checkbox
 *
 * Both the student runner and the admin review screens render questions
 * through here. They used to have separate renderers — admin had a hand-rolled
 * regex markdown parser and an approximate TeX subset — which meant a question
 * could look correct while being written and wrong while being sat. One
 * renderer is the only way that stays true.
 */
import { useMemo } from "react";
import { cn } from "@/lib/cn";
import { renderQuestionHtml } from "./renderQuestion";
import "katex/dist/katex.min.css";
import "./markdown.css";

/**
 * `question` is the 16px/1.75 reading measure the student sees; `ui` is the
 * dense admin size, for solutions and list rows.
 */
export type MarkdownSize = "question" | "ui";

export function QuestionBody({
  source,
  size = "question",
  className,
}: {
  source: string;
  size?: MarkdownSize;
  className?: string;
}) {
  const html = useMemo(() => renderQuestionHtml(source), [source]);

  return (
    <div
      className={cn(
        "markdown-body text-ink",
        size === "question" ? "prose-question" : "text-sm",
        className,
      )}
      // Safe: renderQuestionHtml escapes every code span, code block and raw
      // HTML tag in the source, and only ever emits structure from marked plus
      // KaTeX's and highlight.js's own output. See renderQuestion.ts.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function MarkdownInline({
  source,
  className,
}: {
  source: string;
  className?: string;
}) {
  const html = useMemo(() => renderQuestionHtml(source), [source]);

  return (
    <span
      className={cn("markdown-body markdown-inline text-sm text-ink", className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
